<template>
  <div class="media-form banner">
    <HeadSection :title="isEditMode ? `Item #${bannerId}` : 'Create new banner'" permissionOfSection="banners"
      :breadcrumbs="breadcrumbs" :uuid="bannerUuid">
      <template #activeSwitch>
        <div class="ml-4">
          <a-switch v-model="form.is_active" />
        </div>
      </template>
      <template #actions>
        <a-col :span="14" class="text-right d-flex justify-content-end">
          <div v-if="isEditMode">
            <a-popconfirm title="Sure to delete?" placement="bottom" @confirm.stop="deleteBanner">
              <a-button :loading="loading" type="danger"> Delete </a-button>
            </a-popconfirm>
            <a-button :loading="loading" type="primary" class="ml-4" @click="update">
              Update
            </a-button>
          </div>
          <div v-else class="pt-4">
            <a-button :loading="loading" type="primary" class="ml-4" @click="save">
              Save
            </a-button>
          </div>
        </a-col>
      </template>
    </HeadSection>
    <MainContainer :loading="mainLoading || userLoading" permissionOfSection="banners">
      <!-- <div style="padding: 24px; background-color: #fff"> -->
      <a-form-model :rules="rules" :model="form" ref="form">
        <div class="media-form__main">
          <span class="font-weight-bold font-size-16 mb-3">General</span>
          <a-row>
            <a-col :span="6">
              <a-form-model-item prop="name" :colon="false">
                <template #label>Name <span class="text-gray-7" style="font-weight: 400;">(for internal
                    usage)</span></template>
                <a-input v-model="form.name" placeholder="Name" size="large" :disabled="loading" />
              </a-form-model-item>
            </a-col>
          </a-row>
          <a-row>
            <a-col :span="6">
              <a-form-model-item prop="extId" :colon="false">
                <template #label>External ID</template>
                <a-input v-model="form.extId" placeholder="External id" size="large" :disabled="loading" />
              </a-form-model-item>
            </a-col>
          </a-row>
          <a-row>
            <a-col :span="6">
              <a-form-model-item prop="contentType" label="Content type">
                <a-select v-model="form.contentType" size="large" :disabled="loading" allow-clear>
                  <template #placeholder><span style="color: #a1a1c2">Select content type</span></template>
                  <a-select-option v-for="contentType in contentTypeOptions" :key="contentType.value"
                    :value="contentType.value">
                    {{ contentType.label }}
                  </a-select-option>
                </a-select>
              </a-form-model-item>
            </a-col>
          </a-row>
          <ActivityPeriods ref="activity-periods" :periods="bannerToEdit?.context.context.periods" :showIndex="false"
            :loading="loading" buttonPos="bottom" showLabel useTimezone>
            <template #label>
              Show Period
            </template>
            <template #buttonText>
              + Add new period
            </template>
          </ActivityPeriods>
          <a-form-model-item label="Section" prop="sectionType" :colon="false">
            <a-radio-group name="radioGroup" v-model="form.sectionType" :default-value="form.sectionType"
              :disabled="loading || isSectionTypeDisabled">
              <a-radio value="viewscreens"> Viewscreens </a-radio>
              <a-radio value="screens"> Screens </a-radio>
            </a-radio-group>
          </a-form-model-item>
          <a-row v-if="form.sectionType === 'viewscreens'">
            <a-col :span="6">
              <a-form-model-item label="Viewscreens" prop="selectedViewscreens" :colon="false">
                <a-select v-model="form.selectedViewscreens" size="large" :disabled="loading" mode="multiple" showArrow
                  allow-clear @change="clearApps"
                  :filterOption="(input, option) => option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0">
                  <template #placeholder><span style="color: #a1a1c2;">Select viewscreen</span></template>
                  <a-select-option v-for="viewscreen in viewscreens" :key="viewscreen.id">
                    {{ viewscreen.name }}
                  </a-select-option>
                </a-select>
              </a-form-model-item>
            </a-col>
          </a-row>
          <div v-else>
            <a-row class="mb-3">
              <a-col :span="6">
                <a-form-model-item label="Screens" prop="sections" :colon="false" class="mb-3">
                  <a-select v-model="form.sections" size="large" mode="multiple" showArrow :disabled="loading"
                    allow-clear>
                    <template #placeholder><span style="color: #a1a1c2;">Select screens</span></template>
                    <a-select-option v-for="section in sections" :key="section.value" :value="section.value">
                      {{ section.label }}
                    </a-select-option>
                  </a-select>
                </a-form-model-item>
              </a-col>
            </a-row>
            <a-row>
              <a-col :span="6">
                <a-form-model-item prop="segments" :colon="false">
                  <template #label>
                    <SegmentsLabel />
                  </template>
                  <div class="d-flex align-items-center w-full">
                    <SegmentsSelect v-model="form.segments" :segments="segments" :loading="loading"
                      @change="validateSegments" />
                  </div>
                </a-form-model-item>
              </a-col>
            </a-row>
          </div>
          <div>
            <a-form-model-item prop="apps">
              <div class="mb-4">
                <div class="d-flex align-items-center">
                  <span class="media-form--title required-label mr-3">Apps</span>
                  <a-tooltip v-if="form.sectionType === 'viewscreens'" slot="suffix"
                    title="Inherited from selected viewscreen">
                    <a-icon type="info-circle" class="text-primary" />
                  </a-tooltip>
                  <a v-else @click.stop="openAddAppModal" class="add-btn">+ Add</a>
                </div>
                <AppsCollapse class="mt-2" v-if="form.apps" :apps="form.apps" :appsShown="4" />
              </div>
            </a-form-model-item>
          </div>
         <div>
            <a-form-model-item :colon="false">
              <template #label>
                <span class="mr-1">
                  Display period
                </span>
                <a-tooltip slot="suffix" title="Available for websites only. Not available for mobile apps"
                  :getPopupContainer="getPopupContainer">
                  <a-icon type="info-circle" class="text-primary" />
                </a-tooltip>
              </template>
              <div class="d-flex flex-row align-items-start" style="gap: 1rem">
                <a-form-model-item label="Start date" prop="startDate" :colon="false" class="d-inline-block pr-1">
                  <a-date-picker v-model="form.promo_start" show-time format="YYYY-MM-DD HH:mm:ss" type="date"
                    size="large" placeholder="Select date" :disabled="loading || !isOnlyWebApps" />
                </a-form-model-item>
                <span style="align-self: flex-end;">—</span>
                <a-form-model-item label="End date" prop="endDate" :colon="false" class="d-inline-block pr-1">
                  <a-date-picker v-model="form.promo_end" show-time format="YYYY-MM-DD HH:mm:ss" type="date"
                    size="large" placeholder="Select date" :disabled="loading || !isOnlyWebApps" />
                </a-form-model-item>
              </div>
            </a-form-model-item>
          </div>
          <a-divider class="m-0 mb-3" />
          <div>
            <div class="d-flex flex-row align-items-center justify-content-between mb-2 position-relative">
              <span class="font-weight-bold font-size-16">Media</span>
              <LanguageSelect v-model="selectedLanguage" :langs="languages" :contentFilled="filledLangs"
                :error="formErrorState" short-button-label :style="{ top: 0 }" @copyFromDefault="handleCopyFromDefault"
                @clear="handleClearLang" />
            </div>
            <div>
              <div class="media-form__banner mb-3">
                <div class="d-flex flex-column">
                  <span class="mb-2 media-form--title required-label">Image</span>
                  <div class="mb-3">
                    <a-upload list-type="picture-card" class="media-form__banner--uploader" :show-upload-list="false"
                      @change="(e) => handleImageUpload(e, 'banner')" :disabled="loading">
                      <div v-if="selectedBannerImg">
                        <div v-if="selectedBannerImg.preview" class="media-form__banner--image">
                          <img :src="selectedBannerImg.preview" />
                        </div>
                        <template v-else>
                          <a-icon :type="selectedBannerImg.loading ? 'loading' : 'inbox'" class="text-primary"
                            style="font-size: 48px" />
                          <div>
                            Click or drag file to this area to upload<br />
                            <div v-if="isWebAppChosen" class="text-gray-7">File size 2000 x 640 px</div>
                            <div v-else class="text-gray-7">File size 1160 x 414 px / 1160 x 208 px </div>
                          </div>
                        </template>
                      </div>
                    </a-upload>
                  </div>
                  <div class="d-flex flex-column" style="gap: 1rem;">
                    <div class="">
                      <span class="font-weight-bold font-size-14 mr-3">Redirect</span>
                      <a-switch v-model="selectedRedirect.isRedirectAvailable" @change="handleRedirectSwitch" :disabled="isRedirectDisabled"/>
                    </div>
                    <div v-if="selectedRedirect.isRedirectAvailable">
                      <div class="d-flex flex-column redirect-list">
                        <a-radio-group name="radioGroup" v-model="selectedRedirect.redirectType"
                          :disabled="loading || !isRedirectLinkEmpty" class="mb-3"
                          @change="(event) => handleRedirectTypeChange(event)">
                          <a-radio value="link"> Link </a-radio>
                          <a-radio value="dialogue"> Bottom dialogue </a-radio>
                        </a-radio-group>
                        <div v-if="selectedRedirect.redirectType === 'link'" class="">
                          <a-row>
                            <a-col :span="3" class="mr-4">
                              <a-form-model-item :colon="false">
                                <template #label><span class="required-label">Link type</span></template>
                                <a-select v-model="selectedLinkRedirect.linkType" size="large" :disabled="loading"
                                  allow-clear @change="(value) => handleLinkTypeChange('link')">
                                  <template #placeholder><span style="color: #a1a1c2">Select link type</span></template>
                                  <a-select-option value="custom"> Custom </a-select-option>
                                  <a-select-option value="sport"> Sport </a-select-option>
                                  <a-select-option value="match"> Match </a-select-option>
                                </a-select>
                              </a-form-model-item>
                            </a-col>
                            <a-col :span="7">
                              <div v-if="selectedLinkRedirect.linkType === 'custom'">
                                <a-form-model-item :colon="false">
                                  <template #label>
                                    <span class="required-label">Link</span>
                                    <a-button @click="openAddLinkTemplatesModal" :disabled="isRedirectLinkEmpty"
                                      size="small" type="link"
                                      style="padding-left: 8px !important; height: 15px; line-height: 18px;">Save as a
                                      template</a-button>
                                  </template>
                                  <a-select class="media-form__custom-select" :value="selectedLinkRedirect.link"
                                    :disabled="loading" mode="tags"
                                    @change="(value) => handleTemplateChange(value, 'redirect')"
                                    @inputKeydown="(e) => handleLinkTypeChange('link', e)" optionLabelProp="label"
                                    size="large">
                                    <template #placeholder><span
                                        style="color: #a1a1c2">https://www.olimp.bet/line/1/5956496/78487617</span></template>
                                    <a-select-option v-for="linkTemplate in filteredLinkTemplates"
                                      :key="String(linkTemplate.id)" :value="String(linkTemplate.url)"
                                      :label="linkTemplate.url">
                                      <div class="d-flex justify-content-between align-items-center">
                                        <div class="link-container">
                                          <span class="link-name">{{ linkTemplate.name }}</span>
                                          <span class="link-url">{{ linkTemplate.url || '' }}</span>
                                        </div>
                                        <a-popconfirm overlayClassName="deleteTemplateOverlay" :disabled="false"
                                          :getPopupContainer="getPopupContainer" title="Sure to delete this template?"
                                          @confirm="(e) => deleteLinkTemplate(e, linkTemplate.id)">
                                          <a-icon class="text-danger delete-icon" type="delete" @click.stop />
                                        </a-popconfirm>
                                      </div>
                                    </a-select-option>
                                  </a-select>
                                </a-form-model-item>
                              </div>
                              <div v-if="selectedLinkRedirect.linkType === 'match'">
                                <a-form-model-item :colon="false" label="Match link">
                                  <a-input placeholder="https://www.olimp.bet/line/1/5956496/78487617"
                                    :disabled="loading" v-model="selectedLinkRedirect.link" size="large" />
                                </a-form-model-item>
                              </div>
                              <div v-if="selectedLinkRedirect.linkType === 'sport'">
                                <a-form-model-item :colon="false" label="Sport">
                                  <a-select v-model="selectedLinkRedirect.sportId" size="large" :disabled="loading"
                                    allow-clear>
                                    <template #placeholder><span style="color: #a1a1c2;">Select sport</span></template>
                                    <a-select-option v-for="sport in sports" :key="sport.sport_id">
                                      {{ sport.name }}
                                    </a-select-option>
                                  </a-select>
                                </a-form-model-item>
                              </div>
                            </a-col>
                          </a-row>
                        </div>
                        <div v-else>
                          <div class="d-flex flex-column" style="gap: 1rem">
                            <span class="mb-2 media-form--title">Image</span>
                            <div class="media-form__banner--redirect--uploader">
                              <a-upload list-type="picture-card" :show-upload-list="false"
                                @change="(e) => handleImageUpload(e, 'dialogue')" :disabled="loading"
                                ref="dialogueUploader">
                                <div v-if="selectedDialogueImg">
                                  <div v-if="selectedDialogueImg.preview" class="media-form__banner--image--redirect">
                                    <img :src="selectedDialogueImg.preview" />
                                  </div>
                                  <div v-if="selectedDialogueImg.preview" class="overlay" @click.stop>
                                    <a-button type="link" @click.stop="editSelectedDialogueImg">
                                      <a-icon type="edit" class="text-white font-size-18" />
                                    </a-button>
                                    <a-button type="link" @click.stop="deleteSelectedDialogueImg">
                                      <a-icon type="delete" class="text-white font-size-18" @click.stop />
                                    </a-button>
                                  </div>
                                  <template v-else>
                                    <a-icon :type="selectedDialogueImg.loading ? 'loading' : 'plus'"
                                      class="text-primary" style="font-size: 24px" />
                                    <div>
                                      Upload<br />
                                      <div class="text-gray-7" style="font-size: 12px;">File size 120x120px </div>
                                    </div>
                                  </template>
                                </div>
                              </a-upload>
                            </div>
                            <div class="media-form__banner--redirect__dialogue--title" style="max-width: 650px;">
                              <a-form-model-item :colon="false">
                                <template #label>
                                  <span class="mr-1">
                                    Title
                                  </span>
                                  <a-tooltip slot="suffix" title="This value should be inherited from the card preview"
                                    :getPopupContainer="getPopupContainer">
                                    <a-icon type="info-circle" class="text-primary" />
                                  </a-tooltip>
                                </template>
                                <quill-editor v-model="selectedRedirect.dialogueRedirect.title"
                                  @change="($event) => validateQuillLength($event, 50)" :options="quillEditorOptions"
                                  style="width: 650px;" />
                                <div class="media-form__text-count">{{
                                  stripHtml(selectedRedirect.dialogueRedirect.title).length }} / 50</div>
                              </a-form-model-item>
                            </div>

                          <div class="media-form__banner--redirect__dialogue--title" style="max-width: 650px;">
                              <a-form-model-item :colon="false">
                                <template #label>
                                  <span class="mr-1">
                                    Description
                                  </span>
                                  <a-tooltip slot="suffix" title="Available for websites only. Not available for mobile apps"
                                    :getPopupContainer="getPopupContainer">
                                    <a-icon type="info-circle" class="text-primary" />
                                  </a-tooltip>
                                </template>
                                <a-textarea v-model="selectedRedirect.dialogueRedirect.description" placeholder="Enter title"
                                  :autoSize="{ minRows: 2, maxRows: 4 }" :showCount="true" :maxLength="200" :disabled="loading || !isOnlyWebApps" />
                                  <div class="media-form__text-count">{{
                                  stripHtml(selectedRedirect.dialogueRedirect.description).length }} / 200</div>
                              </a-form-model-item>
                            </div>

                            <div class="media-form__banner--redirect__dialogue--text" style="max-width: 650px;">
                              <span class="text-bold required">Text</span>
                              <quill-editor v-model="selectedRedirect.dialogueRedirect.text"
                                @change="($event) => validateQuillLength($event, 200)" :options="quillEditorOptions"
                                style="width: 650px;" />
                              <div class="media-form__text-count">{{
                                stripHtml(selectedRedirect.dialogueRedirect.text).length }} / 200</div>
                            </div>
                            <a-row>
                              <a-col :span="7">
                                <div class="">
                                  <span class="font-weight-bold font-size-14 mr-3">Button</span>
                                  <a-switch v-model="selectedDialogueRedirect.isDialogueButtonAvailable"
                                    @change="handleDialogueButtonSwitch" />
                                </div>
                              </a-col>
                            </a-row>
                            <a-row v-if="selectedDialogueRedirect.isDialogueButtonAvailable">
                              <a-col :span="4">
                                <a-form-model-item required :colon="false" label="Button name">
                                  <a-input v-model="selectedDialogueButton.buttonName" placeholder="Button name"
                                    size="large" :disabled="loading" />
                                </a-form-model-item>
                              </a-col>
                            </a-row>
                            <a-row v-if="selectedDialogueRedirect.isDialogueButtonAvailable">
                              <a-col :span="3" class="mr-4">
                                <a-form-model-item :colon="false">
                                  <template #label><span class="required-label">Link type</span></template>
                                  <a-select v-model="selectedDialogueButton.linkType" size="large" :disabled="loading"
                                    @change="(value) => handleLinkTypeChange('dialogue')" allow-clear>
                                    <template #placeholder><span style="color: #a1a1c2">Select link
                                        type</span></template>
                                    <a-select-option value="custom"> Custom </a-select-option>
                                    <a-select-option value="sport"> Sport </a-select-option>
                                    <a-select-option value="match"> Match </a-select-option>
                                  </a-select>
                                </a-form-model-item>
                              </a-col>
                              <a-col :span="7">
                                <div v-if="selectedDialogueButton.linkType === 'custom'">
                                  <a-form-model-item :colon="false">
                                    <template #label>
                                      <span class="required-label">Link</span>
                                      <a-button @click="openAddLinkTemplatesModal" :disabled="loading" size="small"
                                        type="link"
                                        style="padding-left: 8px !important; height: 15px; line-height: 18px;">Save as a
                                        template</a-button>
                                    </template>
                                    <a-select class="media-form__custom-select" :value="selectedDialogueButton.link"
                                      :disabled="loading" mode="tags"
                                      @change="(value) => handleTemplateChange(value, 'dialogue')"
                                      @inputKeydown="(e) => handleLinkTypeChange('dialogue', e)" optionLabelProp="label"
                                      size="large">
                                      <template #placeholder><span
                                          style="color: #a1a1c2">https://www.olimp.bet/line/1/5956496/78487617</span></template>
                                      <a-select-option v-for="linkTemplate in filteredLinkTemplates"
                                        :key="linkTemplate.id" :value="String(linkTemplate.url)"
                                        :label="linkTemplate.url">
                                        <div class="d-flex justify-content-between align-items-center">
                                          <div class="link-container">
                                            <span class="link-name">{{ linkTemplate.name }}</span>
                                            <span class="link-url">{{ linkTemplate.url || '' }}</span>
                                          </div>
                                          <a-popconfirm overlayClassName="deleteTemplateOverlay" :disabled="false"
                                            :getPopupContainer="getPopupContainer" title="Sure to delete this template?"
                                            @confirm="(e) => deleteLinkTemplate(e, linkTemplate.id, linkTemplate.url)">
                                            <a-icon class="text-danger delete-icon" type="delete" @click.stop />
                                          </a-popconfirm>
                                        </div>
                                      </a-select-option>
                                    </a-select>
                                  </a-form-model-item>
                                </div>
                                <div v-if="selectedDialogueButton.linkType === 'match'">
                                  <a-form-model-item :colon="false" label="Match link">
                                    <a-input placeholder="https://www.olimp.bet/line/1/5956496/78487617"
                                      :disabled="loading" v-model="selectedDialogueButton.link" size="large" />
                                  </a-form-model-item>
                                </div>
                                <div v-if="selectedDialogueButton.linkType === 'sport'">
                                  <a-form-model-item :colon="false" label="Sport">
                                    <a-select v-model="selectedDialogueButton.sportId" size="large" :disabled="loading"
                                      allow-clear>
                                      <template #placeholder><span style="color: #a1a1c2;">Select
                                          sport</span></template>
                                      <a-select-option v-for="sport in sports" :key="sport.sport_id">
                                        {{ sport.name }}
                                      </a-select-option>
                                    </a-select>
                                  </a-form-model-item>
                                </div>
                              </a-col>
                            </a-row>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </a-form-model>
      <div v-if="errors.oneFullBanner" style="color: red; margin-top: 8px;">At least one language should have image
      </div>
      <div v-if="errors.isLinkRedirectsValid" style="color: red; margin-top: 8px">All link redirects link type and link
        /
        sport itself</div>
      <div v-if="errors.isDialogueRedirectsValid" style="color: red; margin-top: 8px">All dialogue redirects should have
        text</div>
      <div v-if="errors.isdDialogueButtonValid" style="color: red; margin-top: 8px">All dialogue buttons should have
        button
        name, link type and link / sport itself</div>
    </MainContainer>
    <!-- </div> -->
    <a-modal v-model="addAppsModal" title="Add new apps" :footer="null" width="700px" :destroyOnClose="true">
      <AppsModal @selectApps="changeSelectedApps" @close="closeAddAppsModal" :modalAppsData="modalAppsData" />
    </a-modal>
    <a-modal v-model="addLinkTemplateModal" width="395px" :destroyOnClose="true" :footer="null">
      <template #title><span class="modal-title">Add new template</span></template>
      <TemplatesModal :templateLink="selectedRedirect?.link || ''" @addLink="handleAddTemplatesModal"
        @closeTemplateModal="() => addLinkTemplateModal = false" />
    </a-modal>
  </div>
</template>

<script>
import HeadSection from '@/components/main/headSection'
import MainContainer from '@/components/main/mainContainer'
import AppsModal from '@/components/custom/modals/appsModal.vue'
import AppsCollapse from '@/views/apps/media/components/appsCollapse.vue'
import ViewscreenService from '@/services/api/apiViewscreenService'
import ActivityPeriods from '@/components/custom/ActivityPeriods.vue'
import SegmentsSelect from '@/components/custom/segments/segmentsSelect.vue'
import SegmentsLabel from '@/components/custom/segments/segmentsLabel.vue'
import TemplatesModal from '@/components/custom/modals/media/linkTemplates.vue'
import LanguageSelect from '@/components/custom/languageSelect.vue'

import { BANNER_CONTENT_FORM_FIELDS, defaultDialogueButton, defaultBannerRedirect, defaultImg } from '@/constants/media/banners'
import { DEFAULT_LANG } from '@/constants/lang'
import MediaService from '@/services/api/apiMediaService'
import apiClient from '@/services/axios'
import { returnToLastList } from '@/services/utils/routerHelpers'

import { quillEditor } from 'vue-quill-editor'
import { mapState } from 'vuex'
import isEqual from 'lodash/isEqual'

const deepClone = (obj) => {
  return JSON.parse(JSON.stringify(obj))
}

function getBase64(img) {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.addEventListener('load', () => resolve(reader.result))
    reader.readAsDataURL(img)
  })
}

function getImageDimensions(file) {
  return new Promise((resolve) => {
    const img = document.createElement('img')
    img.src = URL.createObjectURL(file)
    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      })
    }
  })
}

export default {
  name: 'BannersForm',
  components: { ActivityPeriods, SegmentsSelect, SegmentsLabel, AppsModal, AppsCollapse, HeadSection, MainContainer, TemplatesModal, quillEditor, LanguageSelect },
  data() {
    return {
      bannerToEdit: undefined,
      languages: [{
        desc: 'Russian',
        id: 0,
        name: 'ru',
      }],
      langOptions: [''],
      linkTemplates: [],
      sports: [],
      segments: [],
      viewscreens: [],
      form: {
        is_active: true,
        name: '',
        extId: '',
        contentType: undefined,
        promo_start: null,
        promo_end: null,
        selectedViewscreens: [],
        sectionType: 'viewscreens',
        sections: [],
        segments: [],
        apps: [],
      },
      activityPeriods: [],
      contentTypeOptions: [
        { label: 'Partners', value: 'Partners' },
        { label: 'Promotion', value: 'Promotion' },
        { label: 'Editorial', value: 'Editorial' },
        { label: 'SMM', value: 'SMM' },
        { label: 'Product', value: 'Product' },
        { label: 'Special project', value: 'Special project' },
      ],
      selectedLanguage: 'ru',
      content: {
        ru: {
          ...BANNER_CONTENT_FORM_FIELDS,
        },
      },
      loading: false,
      mainLoading: false,
      validationResult: true,
      errors: {
        oneFullBanner: false,
        isLinkRedirectsValid: false,
        isDialogueRedirectsValid: false,
        isdDialogueButtonValid: false,
      },
      sections: [
        { label: 'Live', value: 1 },
        { label: 'Line', value: 2 },
        { label: 'Promo', value: 3 },
        { label: 'Main Web', value: 4 },
        { label: 'Registration', value: 5 },
        { label: 'Side menu', value: 6 },
        { label: 'Bottom menu', value: 7 },
      ],
      addAppsModal: false,
      addLinkTemplateModal: false, // TODO DONT FORGET VALID PERIODS
      rules: {
        name: [
          {
            required: true,
            message: 'Banner name is required',
            trigger: 'blur',
          },
        ],
        sectionType: [
          {
            required: true,
            message: 'Please select section type',
            trigger: 'blue',
          },
        ],
        selectedViewscreens: [
          {
            type: 'array',
            required: true,
            message: 'Please select at least one viewscreen',
            trigger: 'change',
          },
        ],
        sections: [
          {
            type: 'array',
            required: true,
            message: 'Please select at least one section',
            trigger: 'change',
          },
        ],
        apps: [
          {
            type: 'array',
            required: true,
            message: 'Please select at least one app',
            trigger: 'change',
          },
        ],
        segments: [
          {
            type: 'array',
            required: true,
            message: 'Please select at least one segment',
            trigger: 'change',
          },
        ],
        activityPeriods: [
          {
            type: 'array',
            required: true,
            message: 'Please enter at least one activity period',
            trigger: 'change',
          },
        ],
      },
      modalAppsData: null,
      quillEditorOptions: {
        placeholder: 'Input description',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'], // toggled buttons
            ['blockquote'],

            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
            [{ direction: 'rtl' }], // text direction
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            [{ color: [] }, { background: [] }], // dropdown with defaults from theme
            [{ align: [] }],

            ['link'],
          ],
        },
      },
    }
  },
  computed: {
    ...mapState(['user']),
    userLoading() {
      return this.$store.state?.user?.loading || false
    },
    isEditMode() {
      return this.$route.path.split('/').includes('edit')
    },
    isWebAppChosen() {
      if (!this.form.apps.length) {
        return false
      }
      return this.form.apps.find((app) => app.bundle_name === 'web.olimpbet' || app.bundle_name === 'web.app (test)')
    },
    selectedContent() {
      return this.content[this.selectedLanguage]
    },
    selectedBannerImg() {
      return this.selectedContent.bannerImg
    },
    selectedRedirect() {
      return this.selectedContent.redirect
    },
    selectedLinkRedirect() {
      return this.selectedRedirect.linkRedirect
    },
    selectedDialogueRedirect() {
      return this.selectedRedirect.dialogueRedirect
    },
    selectedDialogueButton() {
      return this.selectedDialogueRedirect.dialogueButton
    },
    selectedDialogueImg() {
      return this.selectedDialogueRedirect.img
    },
    selectedLanguageId() {
      return this.languages.find((language) => {
        return language.name === this.selectedLanguage
      }).id
    },
    bannerId() {
      if (!this.isEditMode) {
        return null
      }
      return this.$route.params.id
    },
    bannerUuid() {
      if (!this.bannerToEdit) {
        return null
      }
      return this.bannerToEdit.context.context.uuid
    },
    isDefaultContentFilled() {
      const defaultLang = this.content['ru']
      return defaultLang.bannerImg?.preview?.length
    },
    isViewscreen() {
      if (this.$route.params.viewscreenId || this.$route.params.sectionId) {
        return true
      }
      return false
    },
    breadcrumbs() {
      const defaultRootLink = '/media/banners'
      const lastSavedUrl = localStorage?.getItem('lastFilterListUrl')
      const rootLink = lastSavedUrl?.startsWith(defaultRootLink) ? lastSavedUrl : defaultRootLink

      return [
        { name: this.isViewscreen ? 'Main' : 'Media', link: this.isViewscreen ? `/main/${this.$route.params.viewscreenId}` : rootLink },
        { name: this.isEditMode ? 'Edit banner' : 'New banner', link: undefined },
      ]
    },
    isSectionTypeDisabled() {
      return !!this.form.sections.length || !!this.form.selectedViewscreens.length || !!this.form.segments.length
    },
    isRedirectDisabled() {
      return this.form.sectionType === 'viewscreens'
    },
    isRedirectLinkEmpty() {
      return !this.selectedLinkRedirect.link.length
    },
    filteredLinkTemplates() {
      const uniqueTemplates = new Set()
      return this.linkTemplates.filter(template => {
        const url = template.url.trim()
        if (!url || uniqueTemplates.has(url)) {
          return false
        }
        uniqueTemplates.add(url)
        return true
      })
    },
    isInitialForm() {
      return isEqual(this.selectedContent, BANNER_CONTENT_FORM_FIELDS)
    },
    formErrorState() {
      if (this.isInitialForm) {
        return null
      }
      return this.validationResult ? null : 'Form is not filled correctly'
    },
    filledLangs() {
      const obj = this.languages.reduce((obj, { name }) => {
        obj[name] = !isEqual(this.content[name], BANNER_CONTENT_FORM_FIELDS)
        return obj
      }, {})

      return obj
    },
    isOnlyWebApps() {
      return this.form.apps?.length > 0 &&
         this.form.apps.every(app => app.platform === 'web')
    },
  },
  watch: {
    'form.selectedViewscreens'(viewscreenIds) {
      const allApps = viewscreenIds
        .map(id => {
          const vs = this.viewscreens.find(v => v.id === id)
          if (!vs) {
            console.warn('Viewsreen not found:', id, this.viewscreens)
          }
          return vs?.applications || []
        })
        .flat()
      const uniqueApps = new Map()
      allApps.forEach(app => {
        if (app?.application_id) {
          uniqueApps.set(app.application_id, app)
        }
      })
      this.form.apps = Array.from(uniqueApps.values())
      this.validateApps()
      if (!this.form.selectedViewscreens.length) {
        this.form.sections = []
      }
      this.validateApps()
    },
    'form.sectionType'() {
      if (this.mainLoading) return

      this.clearApps()
      if (this.form.sectionType === 'viewscreens') {
        this.handleClearAllLangs()
      }
    },
    selectedContent: {
      deep: true,
      handler() {
        if (this.isInitialForm) {
          return
        }

        this.$nextTick(() => this.validateForm())
      },
    },
    selectedLanguage() {
      if (this.isInitialForm) {
        return
      }

      this.validateForm()
    },
  },
  async created() {
    this.mainLoading = true
    await this.getLocalizations()
    await this.getViewscreens()
    this.getSports()
    this.getLinkTemplates()
    this.getSegments()

    if (this.$route.params.id) {
      this.bannerToEdit = await this.getBanner()
      await this.fillForm()
    }

    if (!this.bannerToEdit) {
      this.selectedLanguage = this.languages[0].name
      this.initializeContent()
    }
    this.mainLoading = false
  },
  methods: {
    getPopupContainer() {
      return document.body
    },
    async validateBannerForm() {
      const isValid = await new Promise((resolve) => {
        this.$refs.form.validate(resolve)
      })
      let atLeastOneValid = false
      let isLinkRedirectValid = true
      let isDialogueRedirectValid = true
      let isDialogueButtonValid = true

      for (const lang in this.content) {
        const currentTab = this.content[lang]

        const isPreviewEmpty = !currentTab.bannerImg?.preview
        if (!isPreviewEmpty) {
          atLeastOneValid = true
        }

        if (currentTab.redirect?.isRedirectAvailable) {
          if (currentTab.redirect.redirectType === 'link') {
            const linkRedirect = currentTab.redirect.linkRedirect || {}

            const hasLinkType = !!linkRedirect.linkType
            const hasValidLinkOrSport =
              (linkRedirect.linkType === 'sport' && !!linkRedirect.sportId) ||
              (linkRedirect.linkType !== 'sport' && !!linkRedirect.link)

            if (!hasLinkType || !hasValidLinkOrSport) {
              isLinkRedirectValid = false
            }
          }

          if (currentTab.redirect.redirectType === 'dialogue') {
            const dialogueRedirect = currentTab.redirect.dialogueRedirect || {}

            if (!dialogueRedirect.text || dialogueRedirect.text.trim() === '') {
              isDialogueRedirectValid = false
            }

            if (dialogueRedirect.isDialogueButtonAvailable) {
              const btn = dialogueRedirect.dialogueButton || {}
              const hasBtnName = !!btn.buttonName && btn.buttonName.trim() !== ''
              const hasBtnLinkType = !!btn.linkType
              const hasBtnLinkOrSport =
                (btn.linkType === 'sport' && !!btn.sportId) ||
                (btn.linkType !== 'sport' && !!btn.link)

              if (!hasBtnName || !hasBtnLinkType || !hasBtnLinkOrSport) {
                isDialogueButtonValid = false
              }
            }
          }
        }
      }

      this.errors = {
        oneFullBanner: !atLeastOneValid,
        isLinkRedirectsValid: !isLinkRedirectValid,
        isDialogueRedirectsValid: !isDialogueRedirectValid,
        isdDialogueButtonValid: !isDialogueButtonValid,
      }

      return isValid && !Object.values(this.errors).includes(true)
    },
    async validateApps() {
      const isValid = await new Promise((resolve) => {
        this.$refs.form.validateField('apps', resolve)
      })
      return isValid
    },
    async validateSegments() {
      const isValid = await new Promise((resolve) => {
        this.$refs.form.validateField('segments', resolve)
      })
      return isValid
    },
    async handleAddTemplatesModal(newTemplate) {
      this.addLinkTemplateModal = false
      this.linkTemplates.push(newTemplate)
    },
    async save() {
      this.loading = true

      const isPeriodsValidate = await this.$refs['activity-periods'].validate()

      const isFormValid = await this.validateBannerForm()
      if (!isFormValid || !isPeriodsValidate) {
        this.loading = false
        return
      }
      return await this.createBanner()
    },
    async update() {
      this.loading = true
      const isPeriodsValidate = await this.$refs['activity-periods'].validate()

      const isFormValid = await this.validateBannerForm()

      if (!isFormValid || !isPeriodsValidate) {
        this.loading = false
        return
      }
      return await this.updateBanner()
    },
    async deleteBanner() {
      try {
        const bannerResponse = await MediaService.deleteBanner(this.bannerId)

        if (!bannerResponse || bannerResponse.success === false) {
          this.$notification.error({
            message: 'Error while deleting banner',
            description: bannerResponse?.data.error || 'Unknown error',
          })
          return
        }
        this.$notification.success({
          message: 'Banner deleted successfully',
        })
        if (this.isViewscreen) {
          return this.redirectToViewscreen()
        }
        this.redirectToMedia()
      } catch (e) {
        this.$notification.error({
          message: 'Error while deleting banner',
          description: e.response.data.error.message,
        })
      }
    },
    async getBanner() {
      try {
        this.loading = true
        const bannerResponse = await MediaService.getBanner(this.$route.params.id)
        this.loading = false
        return bannerResponse.data.data
      } catch (err) {
        this.$notification.error({
          message: err.response.data.error.message,
        })
        this.loading = false
      }
    },
    async getLocalizations() {
      this.languagesLoading = true
      const url = '/admin/localizations'
      try {
        const response = await apiClient.get(url)
        this.languages = response.data.data
        this.langOptions = this.languages.map((lang) => {
          return {
            label: lang.desc,
            value: lang.id,
          }
        })
      } catch (error) {
        console.log('Error while trying to get locales', error)
      } finally {
        this.languagesLoading = false
      }
    },
    async getLinkTemplates() {
      this.loading = true
      try {
        const response = await MediaService.getCustomLinks()
        this.linkTemplates = response.data.data
      } catch (error) {
        console.log('Error while trying to get templates', error)
      } finally {
        this.loading = false
      }
    },
    async getViewscreens() {
      this.loading = true
      try {
        const response = await ViewscreenService.getViewscreenTabs('', 'promo')
        if (response.data.data) {
          this.viewscreens = response.data.data.filter(
            (viewscreen) => !viewscreen.is_default)
        }
      } finally {
        this.loading = false
      }
    },
    async getSports() {
      this.loading = true
      try {
        const response = await MediaService.getSports()
        this.sports = response.data.data
      } catch (error) {
        console.log('Error while trying to get sports', error)
      } finally {
        this.loading = false
      }
    },
    async getPromo(type, promoId) {
      this.loading = true
      try {
        const response = await apiClient.get(`admin/promo/${promoId}`, { params: { type } })
        this.promo = response.data.data
      } catch (error) {
        console.log('Error while trying to fetch promo', error)
      } finally {
        this.loading = false
      }
    },
    async getSegments() {
      this.loading = true
      try {
        const response = await ViewscreenService.getSegmentatorSegments()
        if (response.data.data) {
          this.segments = response.data.data
        }
      } finally {
        this.loading = false
      }
    },
    getBannerImagesByLanguage() {
      return Object.entries(this.content).reduce((acc, [language, content]) => {
        const id = content.bannerImg.id

        if (id === null) {
          return acc
        }

        const bannerInfo = { id }

        const redirect = content.redirect

        if (redirect.isRedirectAvailable) {
          const redirectType = redirect.redirectType

          if (redirectType === 'link') {
            const { linkType, linkName, sportId, link } = redirect.linkRedirect

            bannerInfo.redirect = {
              type: 'link',
              link: {
                type: linkType,
                url: linkType === 'sport' ? null : link || '',
              },
            }
            if (link?.length) {
              bannerInfo.redirect.link.url = link
            }

            if (linkType === 'custom') {
              bannerInfo.redirect.link.name = linkName
            }

            if (linkType === 'sport') {
              bannerInfo.redirect.link.sport_id = sportId
            }
          }
          if (redirectType === 'dialogue') {
            const dialogue = redirect.dialogueRedirect
            const dialogueData = {
              ...(dialogue.title.length !== 0 && { title: dialogue.title }),
              ...(dialogue.description.length !== 0 && { description: dialogue.description }),
              body: dialogue.text || '',
              ...(dialogue.img.preview !== '' && { image_id: dialogue.img.id }),
            }
            if (dialogue.isDialogueButtonAvailable) {
              const btn = dialogue.dialogueButton
              const buttonRedirect = {
                type: 'link',
                link: {
                  type: btn.linkType,
                  url: btn.linkType === 'sport' ? null : btn.link || '',
                },
              }

              if (btn.link?.length) {
                buttonRedirect.link.url = btn.link
              }

              if (btn.linkType === 'custom') {
                buttonRedirect.link.name = btn.linkName
              }

              if (btn.linkType === 'sport') {
                buttonRedirect.link.sport_id = btn.sportId
              }
              dialogueData.button = {
                name: btn.buttonName || '',
                redirect: buttonRedirect,
              }
            }
            bannerInfo.redirect = {
              type: 'dialogue',
              dialogue: dialogueData,
            }
          }
        }

        acc[language] = bannerInfo
        return acc
      }, {})
    },
    getPreviewsByLanguage() {
      return Object.entries(this.content).reduce((acc, [language, content]) => {
        return {
          ...acc,
          [language]: content.preview.id ? [content.preview.id] : [],
        }
      }, {})
    },
    getApplications() {
      return this.form.apps.map((app) => {
        return {
          id: app.application_id,
          ...(app.min_app_version !== '' && { min_app_version: app.min_app_version }),
          ...(app.max_app_version !== '' && { max_app_version: app.max_app_version }),
        }
      })
    },
    getBannerPayload() {
      const bannerPayload = {
        name: this.form.name,
        ...(this.form.extId?.trim() !== '') && { external_id: this.form.extId },
        ...(this.form.contentType !== undefined && { content_type: this.form.contentType }),
        periods: this.$refs['activity-periods'].getPeriods(),
        ...(this.form.sectionType === 'viewscreens' && { viewscreens: this.form.selectedViewscreens }),
        ...(this.form.sectionType === 'screens' && { applications: this.getApplications() }),
        ...(this.form.sectionType === 'screens' && { displays: this.form.sections }),
        ...(this.form.sectionType === 'screens' && { segments: this.form.segments }),
        ...(this.form.promo_start !== null && { promo_start: this.$moment(this.form.promo_start).tz('Etc/UTC').format('YYYY-MM-DD HH:mm:ss') }),
        ...(this.form.promo_end !== null && { promo_end: this.$moment(this.form.promo_end).tz('Etc/UTC').format('YYYY-MM-DD HH:mm:ss') }),
        is_active: this.form.is_active,
        images: this.getBannerImagesByLanguage(),
      }
      return bannerPayload
    },
    async createBanner() {
      try {
        this.mainLoading = true
        const bannerPayload = this.getBannerPayload()

        const bannerResponse = await MediaService.createBanner(bannerPayload)
        if (bannerResponse?.data?.error || bannerResponse.success === false || bannerResponse.data.error) {
          this.$notification.error({
            message: 'Error while creating banner',
            description: bannerResponse?.data?.error?.message || '',
          })
          this.loading = false
          this.mainLoading = false
          return
        }
        this.$notification.success({
          message: 'Banner has been successfully created',
        })
        if (this.isViewscreen) {
          await this.addBannerToViewscreen(bannerResponse.data.data.context.id)
          return this.redirectToViewscreen()
        }
        this.redirectToMedia()
      } catch (e) {
        this.$notification.error({
          message: 'Error while creating banner',
          description: e?.response?.data?.error?.message || '',
        })
      } finally {
        this.loading = false
        this.mainLoading = false
      }
    },
    async updateBanner() {
      try {
        this.mainLoading = true
        const bannerPayload = this.getBannerPayload()

        const updateResponse = await MediaService.updateBanner(
          this.bannerId,
          bannerPayload,
        )
        if (updateResponse?.data?.error) {
          this.$notification.error({
            message: 'Error while updating banner',
            description: updateResponse?.data?.error?.message || '',
          })
          this.mainLoading = false
          this.loading = false
          return
        }
        this.$notification.success({
          message: 'Banner has been successfully updated',
        })
        if (this.isViewscreen) {
          return this.redirectToViewscreen()
        }
        this.redirectToMedia()
      } catch (e) {
        this.$notification.error({
          message: 'Error while updating banner',
          description: e?.response?.data?.error?.message || '',
        })
      } finally {
        this.loading = false
        this.mainLoading = false
      }
    },
    async addBannerToViewscreen(id) {
      this.mainLoading = true
      try {
        const response = await ViewscreenService.addViewscreenSectionItem(this.$route.params.sectionId, id)
        if (response.data.data) {
          this.$notification.success({
            message: 'Banner has been successfully added to the viewscreen section',
          })
        }
      } catch (e) {
        this.$notification.error({
          message: 'Error while adding story to viewscreen',
          description: e.response.data.error.message,
        })
      } finally {
        this.mainLoading = false
      }
    },
    openAddAppModal() {
      if (this.loading) {
        return
      }
      this.addAppsModal = true
    },
    closeAddAppsModal() {
      this.addAppsModal = false
    },
    clearApps() {
      this.form.apps = []
      this.validateApps()
      this.modalAppsData = null
    },
    openAddLinkTemplatesModal() {
      if (this.loading) {
        return
      }
      this.addLinkTemplateModal = true
    },
    handleTemplateChange(newTemplate, type) {
      const filtered = (newTemplate || []).filter(template => template !== '' && template !== undefined)
      const last = filtered[filtered.length - 1] || []
      const foundTemplate = this.linkTemplates.find(link => link.url === last)
      const templateName = foundTemplate ? foundTemplate.name : ''

      const target = type === 'redirect' ? this.selectedLinkRedirect : this.selectedDialogueButton
      target.link = last
      target.linkName = templateName
    },
    handleLinkTypeChange(type, event) {
      if (event && event.key !== undefined) {
        if (event.key.length !== 1) return
      }

      const target = type === 'link'
        ? this.selectedLinkRedirect
        : this.selectedDialogueButton

      Object.assign(target, { link: [], linkName: '', sportId: '' })
    },
    async deleteLinkTemplate(event, templateId, deletedlink) {
      try {
        const linkResponse = await MediaService.deleteLink(templateId)

        if (!linkResponse || linkResponse.success === false) {
          this.$notification.error({
            message: 'Error while deleting link',
            description: linkResponse?.data.error || 'Unknown error',
          })
          return
        }
        this.$notification.success({
          message: 'Link deleted successfully',
        })
        this.linkTemplates = this.linkTemplates.filter(link => link.id !== templateId)
        if (this.selectedLinkRedirect.link === deletedlink) {
          this.selectedLinkRedirect.link = []
        }
        if (this.selectedDialogueButton.link === deletedlink) {
          this.selectedDialogueButton.link = []
        }
      } catch (e) {
        this.$notification.error({
          message: 'Error while deleting link',
          description: e.response.data.error.message,
        })
      }
      event.stopPropagation()
    },
    handleLangChange(selectedLangKey) {
      if (this.loading) {
        return
      }
      this.selectedLanguage = selectedLangKey
    },
    handleRedirectSwitch(checked) {
      if (!checked) {
        this.content[this.selectedLanguage].redirect = deepClone(defaultBannerRedirect)
      }
    },
    handleRedirectTypeChange(event) {
      this.content[this.selectedLanguage].redirect = deepClone(defaultBannerRedirect)
      this.content[this.selectedLanguage].redirect.isRedirectAvailable = true
      this.content[this.selectedLanguage].redirect.redirectType = event.target.value
    },
    handleDialogueButtonSwitch(checked) {
      if (!checked) {
        this.content[this.selectedLanguage].redirect.dialogueRedirect.dialogueButton = deepClone(defaultDialogueButton)
      }
    },
    async handleImageUpload(event, type) {
      if (event.file.status === 'uploading') {
        return
      }

      const file = event.file

      const isFileValid = await this.validateUploadedImage(
        event.file.originFileObj,
        type,
      )

      if (!isFileValid) {
        return
      }

      const fileType = file.type.split('/')[0]
      const fileId = file.uid

      const target =
        type === 'banner'
          ? this.content[this.selectedLanguage].bannerImg
          : this.content[this.selectedLanguage].redirect.dialogueRedirect.img

      Object.assign(target, {
        ...defaultImg,
        id: fileId,
        loading: true,
        file,
      })

      try {
        const resp = await MediaService.uploadImage(file)
        const imgId = resp.data?.data?.id
        if (!imgId) {
          const errorMessage =
            resp.data?.error?.message ||
            'Something went wrong while uploading image'
          Object.assign(target, { ...defaultImg })
          this.$notification.error({
            message: errorMessage,
          })
          return
        }
        if (fileType === 'image') {
          target.id = imgId
          target.preview = await getBase64(event.file.originFileObj)
          target.loading = false
        }
      } catch (e) {
        const errorMessage =
          e.response.data?.error?.message ||
          'Something went wrong while uploading image'
        Object.assign(target, { ...defaultImg })
        this.$notification.error({
          message: errorMessage,
        })
      }
    },
    async validateUploadedImage(file, type) {
      const isImage = file.type.split('/')[0] === 'image'
      const fileExtension = file.type.split('/')[1]

      if (!isImage) {
        this.$notification.error({
          message: 'You can only upload image file!',
        })
        return false
      }

      const isLt10M = file.size / 1024 / 1024 < 10

      if (!isLt10M) {
        this.$notification.error({
          message: 'Image must be smaller than 10MB!',
        })
        return false
      }

      const dimensionRules = {
        dialogue: {
          sizes: [[120, 120]],
          message: 'Dialogue image must be 120x120!',
        },
        banner: this.isWebAppChosen
          ? {
            sizes: [[2000, 640]],
            message: 'Banner must be 2000x640 for Web app!',
          }
          : {
            sizes: [
              [1160, 414],
              [1160, 208],
            ],
            message: 'Banner must be 1160×414 or 1160×208!',
          },
      }

      if (isImage) {
        const { width, height } = await getImageDimensions(file)
        const rule = dimensionRules[type]

        const isValidSize = rule?.sizes.some(
          ([w, h]) => width === w && height === h,
        )

        if (!isValidSize) {
          this.$notification.error({ message: rule?.message || 'Invalid image size' })
          return false
        }
        const allowedExtensions = ['png', 'webp', 'jpg', 'jpeg']

        if (!allowedExtensions.includes(fileExtension.toLowerCase())) {
          this.$notification.error({
            message: 'Image can be PNG, WEBP, or JPG / JPEG extension!',
          })
          return false
        }
      }
      return file
    },
    editSelectedDialogueImg() {
      const uploadComponent = this.$refs.dialogueUploader
      if (uploadComponent) {
        const fileInput =
          uploadComponent.$el.querySelector('input[type="file"]')

        if (fileInput) {
          fileInput.click()
        }
      }
    },
    deleteSelectedDialogueImg() {
      this.selectedDialogueImg.file = null
      this.selectedDialogueImg.id = null
      this.selectedDialogueImg.loading = false
      this.selectedDialogueImg.preview = ''
    },
    handleCopyFromDefault() {
      if (!this.content[DEFAULT_LANG] || !this.content[this.selectedLanguage]) {
        return
      }

      const defaultForm = this.content[DEFAULT_LANG]

      this.content[this.selectedLanguage] = JSON.parse(
        JSON.stringify(defaultForm),
      )
    },
    initializeContent() {
      this.content = this.languages.reduce((acc, language) => {
        return {
          ...acc,
          [language.name]: {
            bannerImg: deepClone(defaultImg),
            redirect: deepClone(defaultBannerRedirect),
          },
        }
      }, {})
    },
    validateQuillLength(e, maxLength) {
      const text = e.text.trim() ?? ''

      if (maxLength && maxLength < text.length) {
        e.quill.setText(text.substring(0, maxLength))
      }
    },
    stripHtml(htmlString) {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = htmlString

      return tempDiv.textContent || tempDiv.innerText || ''
    },
    fillForm() {
      const { name, external_id, content_type, segments, applications, displays, is_active, images, promo_start, promo_end } = this.bannerToEdit.context.context
      this.form.name = name
      this.form.extId = external_id
      this.form.contentType = content_type
      this.form.apps = applications
      this.modalAppsData = applications
      if (this.bannerToEdit.context.viewscreens.length) {
        this.form.selectedViewscreens = this.bannerToEdit.context.viewscreens
      } else {
        this.form.sectionType = 'screens'
        this.form.segments = segments.map(segment => {
          return segment.id
        })
      }

      if (displays.length) {
        this.form.sections = displays.map((display) => {
          return display.id
        })
      }

      promo_start ? this.form.promo_start = this.$moment(promo_start).tz('Etc/GMT-3').format('YYYY-MM-DD HH:mm:ss') : this.form.promo_start = null
      promo_end ? this.form.promo_end = this.$moment(promo_end).tz('Etc/GMT-3').format('YYYY-MM-DD HH:mm:ss') : this.form.promo_end = null

      this.form.is_active = is_active

      const bannerLanguages = Object.keys(images)

      const bannerContent = bannerLanguages.reduce((acc, language) => {
        const banners = images[language]

        if (!banners.length) return acc
        const banner = banners[0]
        const redirect = banner.redirect || {}
        const content = {
          bannerImg: {
            id: banner.id,
            preview: banner.path,
            url: banner.path,
          },
          redirect: {
            isRedirectAvailable: !!redirect.type,
            redirectType: redirect.type || 'link',
            linkRedirect: {
              linkType: 'custom',
              linkName: '',
              sportId: '',
              link: [],
              id: '',
            },
            dialogueRedirect: {
              img: {
                id: null,
                preview: '',
                url: '',
              },
              title: '',
              text: '',
              description: '',
              isDialogueButtonAvailable: false,
              dialogueButton: {
                buttonName: '',
                linkType: 'custom',
                linkName: '',
                sportId: '',
                link: [],
                id: '',
              },
            },
          },
        }

        if (redirect.type === 'link') {
          const link = redirect.link || {}
          content.redirect.linkRedirect = {
            linkType: link.type || 'custom',
            linkName: link.name || '',
            sportId: link.sport_id || '',
            link: link.url || '',
            id: link.id || '',
          }
        }

        if (redirect.type === 'dialogue') {
          const dialogue = redirect.dialogue || {}
          content.redirect.dialogueRedirect.title = dialogue.title || ''
          content.redirect.dialogueRedirect.description = dialogue.description || ''
          content.redirect.dialogueRedirect.text = dialogue.body || ''
          if (dialogue.image?.id) {
            content.redirect.dialogueRedirect.img = {
              id: dialogue.image.id,
              preview: dialogue.image.path,
              url: dialogue.image.path,
            }
          }

          if (dialogue.button) {
            content.redirect.dialogueRedirect.isDialogueButtonAvailable = true
            content.redirect.dialogueRedirect.dialogueButton.buttonName = dialogue.button.name || ''

            const btnRedirect = dialogue.button.redirect?.link || {}
            content.redirect.dialogueRedirect.dialogueButton.linkType = btnRedirect.type || 'custom'
            content.redirect.dialogueRedirect.dialogueButton.linkName = btnRedirect.name || ''
            content.redirect.dialogueRedirect.dialogueButton.link = btnRedirect.url || ''
            content.redirect.dialogueRedirect.dialogueButton.sportId = btnRedirect.sport_id || ''
          }
        }

        acc[language] = content
        return acc
      }, {})
      const defaultContent = this.languages.reduce((acc, lang) => {
        acc[lang.name] = {
          bannerImg: { ...defaultImg },
          redirect: { ...defaultBannerRedirect },
        }
        return acc
      }, {})
      this.content = {
        ...defaultContent,
        ...bannerContent,
      }
    },
    changeSelectedApps(apps) {
      this.closeAddAppsModal()
      this.form.apps = apps
      this.modalAppsData = apps
      this.validateApps() // TODO : UPDATE
    },
    redirectToMedia() {
      returnToLastList(this.$router, '/media/banners')
    },
    redirectToViewscreen() {
      this.$router.push(`/main/${this.$route.params.viewscreenId}`)
    },
    redirectToRoute() {
      if (this.isViewscreen) {
        return this.redirectToViewscreen()
      }
      return this.redirectToMedia()
    },
    handleClearLang(lang) {
      this.content[lang] = Object.assign({}, BANNER_CONTENT_FORM_FIELDS)

      this.$refs.form.clearValidate()
    },
    handleClearAllLangs() {
      Object.keys(this.content).forEach(lang => {
        this.content[lang].redirect = deepClone(defaultBannerRedirect)
      })

      this.$refs.form.clearValidate()
    },
    validateForm() {
      if (!this.$refs.form) return

      this.$refs.form.validate((valid) => {
        this.validationResult = valid
      })
    },
  },
}
</script>

<style lang="scss" src="../../style.module.scss" />
